/**
 * 登录态 store（add-auth-login）。
 *
 * 登录成功 → 保存 token（localStorage）+ account/employee/role/permissions（内存）；
 * 页面刷新 → 从 localStorage 恢复 token，身份信息在守卫中调 auth-me 重新拉取。
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AuthAccountInfo, MeOutput } from '@cms/shared'
import { clearSession, getSessionToken, setSessionToken } from '@/utils/auth-session'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getSessionToken())
  const account = ref<AuthAccountInfo | null>(null)
  const employee = ref<MeOutput['employee']>(null)
  const role = ref<MeOutput['role']>(null)
  const roles = ref<NonNullable<MeOutput['roles']>>([])
  const roleCodes = ref<string[]>([])
  const accessChannel = ref<MeOutput['accessChannel']>(null)
  const permissions = ref<string[]>([])
  const tenantService = ref<MeOutput['tenantService']>(undefined)
  const mustChangePassword = ref(false)

  const isLoggedIn = computed(() => !!token.value)
  const roleCode = computed(() => role.value?.code ?? roleCodes.value[0] ?? null)

  function setLogin(payload: { token: string; account: AuthAccountInfo; employee: MeOutput['employee']; role: MeOutput['role']; roles?: MeOutput['roles']; roleCodes?: string[]; accessChannel?: MeOutput['accessChannel']; permissions: string[]; tenantService?: MeOutput['tenantService']; mustChangePassword: boolean }): void {
    token.value = payload.token
    setSessionToken(payload.token)
    account.value = payload.account
    employee.value = payload.employee
    role.value = payload.role
    roles.value = payload.roles ?? (payload.role ? [payload.role] : [])
    roleCodes.value = payload.roleCodes ?? roles.value.map((item) => item.code)
    accessChannel.value = payload.accessChannel ?? null
    permissions.value = payload.permissions
    tenantService.value = payload.tenantService
    mustChangePassword.value = payload.mustChangePassword
  }

  /** 从 auth-me 恢复完整身份（刷新页面后）。 */
  function setIdentity(me: MeOutput): void {
    account.value = me.account
    employee.value = me.employee
    role.value = me.role
    roles.value = me.roles ?? (me.role ? [me.role] : [])
    roleCodes.value = me.roleCodes ?? roles.value.map((item) => item.code)
    accessChannel.value = me.accessChannel ?? null
    permissions.value = me.permissions
    tenantService.value = me.tenantService
    mustChangePassword.value = me.mustChangePassword
  }

  function setMustChangePassword(v: boolean): void {
    mustChangePassword.value = v
  }

  function logout(): void {
    token.value = null
    clearSession()
    account.value = null
    employee.value = null
    role.value = null
    roles.value = []
    roleCodes.value = []
    accessChannel.value = null
    permissions.value = []
    tenantService.value = undefined
    mustChangePassword.value = false
  }

  /** 权限点校验：admin 通配；声明权限点列表含 code 即放行。 */
  function hasPermission(code: string): boolean {
    if (roleCodes.value.includes('admin') || role.value?.code === 'admin' || roles.value.some((item) => item.code === 'admin')) return true
    return permissions.value.includes(code) || permissions.value.includes('*')
  }

  return {
    token,
    account,
    employee,
    role,
    roles,
    roleCodes,
    accessChannel,
    permissions,
    tenantService,
    mustChangePassword,
    isLoggedIn,
    roleCode,
    setLogin,
    setIdentity,
    setMustChangePassword,
    logout,
    hasPermission,
  }
})
